import { useCallback, useState } from "react";

import { useGlobalContext } from "../Context/GlobalContext";

const initialPricing = {
  subTotal: 0,
  cgst: 0,
  sgst: 0,
  total: 0,
  paymentMode: "",
  deliveryFee: 0,
};

const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

export const useOrderDetails = (orderId) => {
  const { anlookApi } = useGlobalContext();

  const [orderDetails, setOrderDetails] = useState(null);

  const [products, setProducts] = useState([]);

  const [pricing, setPricing] = useState(initialPricing);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  // =====================================================
  // GET ORDER DETAILS
  // =====================================================

  const fetchOrderDetails = useCallback(async () => {
    if (!orderId) {
      setOrderDetails(null);
      setProducts([]);
      setPricing(initialPricing);
      setError("");

      return null;
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        orderid: String(orderId),
      };

      console.log("GET ORDER DETAILS PAYLOAD:", payload);

      const res = await anlookApi.post(
        "/api/Order/v1.0/getOrderDetails",
        payload,
      );

      if (res?.data?.responseCode !== "00") {
        throw new Error(
          res?.data?.responseMessage || "Order details fetch nahi ho payi.",
        );
      }

      const order = res?.data?.response || null;

      if (!order) {
        setOrderDetails(null);
        setProducts([]);
        setPricing(initialPricing);

        return null;
      }

      // =====================================================
      // ORDER DETAILS
      // =====================================================

      setOrderDetails(order);

      // =====================================================
      // PRICING
      // =====================================================

      setPricing({
        subTotal: toNumber(order?.subtotal),

        cgst: toNumber(order?.tax_cgst),

        sgst: toNumber(order?.tax_sgst),

        total: toNumber(order?.pay_amount || order?.totalOrderPrice),

        paymentMode: order?.payment_mode || "Online",

        deliveryFee: toNumber(order?.delivery_fee),
      });

      // =====================================================
      // ORDER ITEMS
      // =====================================================

      const mappedProducts = Array.isArray(order?.items)
        ? order.items.map((item, index) => {
            const quantity = toNumber(item?.quantity);

            const unitPrice = toNumber(item?.item_price);

            return {
              id: item?.item_id || item?.id || `${orderId}-${index}`,

              name:
                item?.item_name ||
                item?.actualName ||
                item?.product_id ||
                "Item",

              qty: quantity,

              unitPrice,

              itemTotal: toNumber(item?.total_price || unitPrice * quantity),

              image: item?.itemImage || null,

              customization: item?.customization || "{}",

              cakeCustomization: item?.cakeCustomization || null,
            };
          })
        : [];

      setProducts(mappedProducts);

      return order;
    } catch (error) {
      console.error("GET ORDER DETAILS ERROR:", error);

      setError(
        error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Order details fetch nahi ho payi.",
      );

      setOrderDetails(null);
      setProducts([]);
      setPricing(initialPricing);

      return null;
    } finally {
      setLoading(false);
    }
  }, [orderId, anlookApi]);

  // =====================================================
  // RETURN
  // =====================================================

  return {
    orderDetails,
    products,
    pricing,
    loading,
    error,

    fetchOrderDetails,

    fetchOrderItems: fetchOrderDetails,
  };
};
